/**
 * /wiki/new — Confluence / Notion-style "create a doc immediately".
 *
 * Wave-2B flow: the modal-based new-doc surface is gone. Hitting
 * this route from any deep link (email CTA, slash command, programmatic
 * navigation) fires `useCreateDoc()` once on mount and redirects to the
 * fresh doc's page with `?focusTitle=1` so the doc page auto-focuses +
 * selects the inline title input. The user types their title there and
 * tabs into the editor body — same UX as Confluence + Notion.
 *
 * The internal "New doc" button in `WikiSecondaryRail` uses the same
 * mutation hook directly (no route round-trip) for snappier feedback.
 * This route exists for deep-link / external callers — keep it cheap
 * (mount → create → redirect).
 *
 * Failure path: a creation failure routes back to `/wiki` with a toast,
 * matching the empty-state experience the user would have hit if they
 * never clicked "New doc". We deliberately don't surface an error UI on
 * this route — it should never be visible for more than the BE call's
 * round-trip latency.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useToast } from '@/components/ToastProvider'
import { useCreateDocAndOpen } from '@/features/wiki/hooks/use-create-doc-and-open'

export const Route = createFileRoute('/wiki/new')({
  component: DocsNewComponent,
})

function DocsNewComponent() {
  const { t } = useTranslation('wiki')
  const navigate = useNavigate()
  const toast = useToast()
  const createAndOpen = useCreateDocAndOpen()

  // Strict-mode + route-remount guard: the create mutation should fire
  // exactly once per mount, not twice. Without this guard, dev-mode's
  // double-effect would create two empty "Untitled" rows; production
  // would still only create one, but the guard is cheap insurance.
  const firedRef = useRef(false)

  useEffect(() => {
    if (firedRef.current) return
    firedRef.current = true
    void (async () => {
      try {
        await createAndOpen()
      } catch {
        toast.error(t('doc.createFailureToast'))
        navigate({ to: '/wiki', replace: true })
      }
    })()
    // The mutation hook + navigate + toast + t are stable across renders;
    // re-running this effect would re-fire the create. The firedRef guard
    // makes the deps moot, but listing them keeps the lint happy.
  }, [createAndOpen, navigate, toast, t])

  // Minimal loading sliver — the redirect lands inside ~200ms on a warm
  // Lambda. Showing the "Loading..." copy in the existing wiki namespace
  // keeps the surface localized + recognizable as a doc-loading screen.
  return (
    <div className="flex h-full min-h-0 flex-1 items-center justify-center p-8">
      <p className="text-sm text-fg-muted">{t('doc.loading')}</p>
    </div>
  )
}
